import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { MainColor, ContentMinHeight } from "../style/styleValue";

import { localLogin } from "../api/authAPI";
import { useMessages } from "../i18n";

const ko = {
  title: "로그인",
  subtitle: "계정 정보를 입력하여 로그인해주세요.",
  username: "아이디",
  usernamePh: "아이디를 입력하세요",
  password: "비밀번호",
  passwordPh: "비밀번호를 입력하세요",
  submit: "로그인",
  submitting: "로그인 중…",
  required: "아이디와 비밀번호를 입력해주세요.",
  failed: "아이디 또는 비밀번호를 확인해주세요.",
  noAccount: "아직 계정이 없으신가요?",
  signup: "회원가입",
};

const en: typeof ko = {
  title: "Log In",
  subtitle: "Please enter your account information to log in.",
  username: "Username",
  usernamePh: "Enter your username",
  password: "Password",
  passwordPh: "Enter your password",
  submit: "Log in",
  submitting: "Logging in…",
  required: "Please enter your username and password.",
  failed: "Please check your username and password.",
  noAccount: "Don't have an account yet?",
  signup: "Sign up",
};

const Login = () => {
  const [id, setId] = useState<string>("");
  const [pw, setPw] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const t = useMessages({ ko, en });

  const clickLoginBtn = async () => {
    if (!id.trim() || !pw.trim()) {
      setErrorMsg(t.required);
      return;
    }

    setIsSubmitting(true);
    const result = await localLogin(id, pw);
    setIsSubmitting(false);

    if (result) {
      const from = (location.state as any)?.from || { pathname: "/" };
      navigate(from, { replace: true });
    } else {
      setErrorMsg(t.failed);
    }
  };

  const handleChange = (
    setter: (v: string) => void
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    if (errorMsg) setErrorMsg("");
  };

  return (
    <PageWrapper>
      <FormCard>
        <HeaderArea>
          <LogoMark>+</LogoMark>
          <Title>{t.title}</Title>
          <SubText>{t.subtitle}</SubText>
        </HeaderArea>

        <FormArea
          onSubmit={(e) => {
            e.preventDefault();
            clickLoginBtn();
          }}
        >
          <FieldGroup>
            <FieldLabel>{t.username}</FieldLabel>
            <InputWrapper>
              <UserOutlined className="icon" />
              <Input
                type="text"
                name="username"
                placeholder={t.usernamePh}
                value={id}
                onChange={handleChange(setId)}
                autoComplete="username"
              />
            </InputWrapper>
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>{t.password}</FieldLabel>
            <InputWrapper>
              <LockOutlined className="icon" />
              <Input
                type="password"
                name="password"
                placeholder={t.passwordPh}
                value={pw}
                onChange={handleChange(setPw)}
                autoComplete="current-password"
              />
            </InputWrapper>
          </FieldGroup>

          {errorMsg && <ErrorMessage>{errorMsg}</ErrorMessage>}

          <LoginButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? t.submitting : t.submit}
          </LoginButton>
        </FormArea>

        <FooterLinks>
          {t.noAccount}{" "}
          <span onClick={() => navigate("/signup")}>{t.signup}</span>
        </FooterLinks>
      </FormCard>
    </PageWrapper>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: ${ContentMinHeight};
  padding: 40px 20px;
  background: #f0f4f8;
`;

const FormCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid #e8edf2;
  display: flex;
  flex-direction: column;
  gap: 28px;
  animation: ${fadeIn} 0.4s ease;

  @media (max-width: 600px) {
    padding: 28px 20px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }
`;

const HeaderArea = styled.div`
  text-align: center;
`;

const LogoMark = styled.div`
  width: 48px;
  height: 48px;
  background: ${MainColor};
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #17375f;
  margin-bottom: 6px;
`;

const SubText = styled.p`
  font-size: 14px;
  color: #888;
  line-height: 1.5;
`;

const FormArea = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #555;
  letter-spacing: 0.02em;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;

  .icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #a0a0a0;
    font-size: 16px;
    transition: color 0.3s;
    z-index: 1;
  }

  &:focus-within .icon {
    color: #17375f;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px 12px 42px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  outline: none;
  transition: all 0.2s;
  color: #333;

  &::placeholder {
    color: #bbb;
  }

  &:focus {
    background: #fff;
    border-color: #17375f;
    box-shadow: 0 0 0 3px rgba(23, 55, 95, 0.08);
  }
`;

const ErrorMessage = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  text-align: center;
  background: #fff1f0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #ffa39e;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 8px;
  background: ${MainColor};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: #102a4a;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    background: #b0bec5;
    cursor: not-allowed;
  }
`;

const FooterLinks = styled.div`
  text-align: center;
  font-size: 13px;
  color: #888;

  span {
    color: #17375f;
    font-weight: 700;
    cursor: pointer;
    margin-left: 4px;
    text-decoration: underline;

    &:hover {
      color: #0050b3;
    }
  }
`;

export default Login;
